'use client';

import { ExternalLink } from 'lucide-react';
import Image from 'next/image';
import { cn } from '../lib/utils';
import type { Publication } from '../types/publication';
import { highlightText } from '../utils/highlight-text';
import { buttonVariants } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

interface PublicationCardProps {
  publication: Publication;
  /** Search term for highlighting text */
  searchTerm?: string;
}

/**
 * Format edition date from date string
 */
function formatEditionDate(date: Date | null | undefined): string | null {
  if (!date) {
    return null;
  }

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function PublicationCard({ publication, searchTerm }: PublicationCardProps) {
  const title = publication.title || publication.pubRef || 'Publication';
  const href = publication.pubUrl ?? undefined;
  const thumbUrl = publication.thumbnail?.url;
  const thumbAlt = publication.thumbnail?.alt || title;
  const ref = publication.pubRef;
  const editionDate = formatEditionDate(publication.editionDate || null);

  // Helper to highlight text if search term exists
  const highlight = (text: string) => {
    if (!text) return '';
    if (!searchTerm) return text;
    return highlightText(text, searchTerm);
  };

  const handleImageClick = () => {
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Card className="group w-full rounded-sm flex flex-row h-full border-none overflow-hidden shadow-md bg-slate-500/5 transition hover:shadow-md focus-within:ring-2 focus-within:ring-ring">
      <div
        className="shrink-0 relative cursor-pointer"
        onClick={handleImageClick}
        role={href ? 'button' : undefined}
        tabIndex={href ? 0 : undefined}
        onKeyDown={e => {
          if (href && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleImageClick();
          }
        }}
      >
        {thumbUrl ? (
          <div className="relative w-32 h-42 bg-muted overflow-hidden">
            <Image
              src={thumbUrl}
              alt={thumbAlt}
              fill
              className="object-cover transition-transform duration-300 group-hover:scale-[1.04]"
            />
            {/* Overlay and button on hover */}
            {href && (
              <>
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={handleButtonClick}
                          className={cn(
                            buttonVariants({ variant: 'outline', size: 'lg' }),
                            'bg-background/95 backdrop-blur-sm hover:bg-background'
                          )}
                          aria-label="Open publication in a new tab"
                        >
                          <ExternalLink className="h-5 w-5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="text-xs max-w-xs">
                        <div className="font-semibold">Open</div>
                        <div>in a new tab</div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="relative w-32 h-full bg-muted flex items-center justify-center text-sm text-muted-foreground">
            No preview
          </div>
        )}
      </div>
      <div className="flex flex-col flex-1 min-w-0">
        <CardHeader className="py-3">
          <CardTitle className="text-base font-semibold leading-tight line-clamp-4">{highlight(title)}</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 text-xs text-muted-foreground hidden">
          <div className="flex items-center justify-between">
            {ref && <span>{highlight(ref)}</span>}
            {editionDate && <span suppressHydrationWarning>{editionDate}</span>}
          </div>
        </CardContent>
      </div>
    </Card>
  );
}

